import { MDText } from 'i18n-react';
import Link from 'next/link';

import LazyLoadImage from 'Components/shared/LazyLoadImage/LazyLoadImage';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { triggerRecommendedVehicleCardClicked } from 'Services/analytics/events';
import type { VehicleListItem } from 'Types/vehicle';
import { formatPrice } from 'Utilities/formatPrice';
import { formatVehicleName } from 'Utilities/formatters';
import ROUTES from 'Utilities/routes';

import SimilarVehiclesSectionTexts from '../SimilarVehiclesSection.texts';

import styles from './SimilarVehiclesCard.module.scss';

const imageWidth = 155;
const imageHeight = 115;

const localT = new MDText(SimilarVehiclesSectionTexts);

interface SimilarVehiclesCardProps {
	displayPrice: VehicleListItem['displayPrice'];
	id: VehicleListItem['id'];
	isVatQualifying: VehicleListItem['isVatQualifying'];
	leadPhoto: VehicleListItem['leadPhoto'];
	make: VehicleListItem['make'];
	model: VehicleListItem['model'];
}

export const SimilarVehiclesCard: React.FC<SimilarVehiclesCardProps> = ({
	displayPrice,
	id,
	isVatQualifying,
	leadPhoto,
	make,
	model,
}) => {
	const { id: currentPageVehicleId } = useVehicleData();

	const onClick = () => {
		triggerRecommendedVehicleCardClicked({ id, url: ROUTES.VEHICLE.as(currentPageVehicleId) });
	};

	return (
		<Link
			as={ROUTES.VEHICLE.as(id)}
			className={styles.link}
			href={`${ROUTES.VEHICLE.href}?vehicleId=${id}`}
			id={`vehicle_card_${id}`}
			onClick={onClick}
			onKeyUp={onClick}
			tabIndex={0}
		>
			<LazyLoadImage
				classNames={{ container: styles.imageContainer, image: styles.image }}
				height={imageHeight}
				photo={leadPhoto}
				rootComponent="div"
				width={imageWidth}
			/>
			<h5 className={styles.title}>{formatVehicleName({ make, model })}</h5>
			<div className={styles.subtitle}>
				<span className={styles.status} />
				<h6 className={styles.price}>
					{localT.translate('price', {
						price: formatPrice(displayPrice, false, 0),
						suffix: isVatQualifying ? '+VAT' : '',
					})}
				</h6>
			</div>
		</Link>
	);
};
